import type { Breadcrumb } from '@models/Breadcrumbs';
import type { NavTree, NavTreeItem } from '@models/NavigationTree';
import { createTreeIterator } from '@utils/tree-iterator';
import { getFlattenedPathFromSourceFilePath } from '@utils/common';

export function findNavTreeItemByHref(navTree: NavTree, href: string): NavTreeItem | null {
  for (const navTreeItem of createTreeIterator<NavTreeItem>(navTree)) {
    if (navTreeItem.href === href) {
      return navTreeItem;
    }
  }

  return null;
}

function findParentNavItem(navTreeItem: NavTreeItem, slug: string): NavTreeItem | null {
  if (navTreeItem.children === undefined) {
    return null;
  }

  for (const navSubtreeItem of navTreeItem.children) {
    if (navSubtreeItem?.meta?.slug === slug) {
      return navTreeItem;
    }

    const item = findParentNavItem(navSubtreeItem, slug);
    if (item) {
      return item;
    }
  }

  return null;
}

function findParentNavTreeItemBySlug(navTree: NavTree, slug: string): NavTreeItem | null {
  for (const navItem of navTree) {
    const item = findParentNavItem(navItem, slug);
    if (item) {
      return item;
    }
  }

  return null;
}

export function getBreadCrumbsArrayForPage(navTree: NavTree, slug?: string | string[]): Breadcrumb[] {
  if (!slug || !slug.length) {
    return [];
  }

  const slugArray = Array.isArray(slug) ? slug : [slug];
  const href = `/${slugArray.join('/')}`;

  const breadcrumbs: Breadcrumb[] = [];

  let navTreeItem = findNavTreeItemByHref(navTree, href);

  while (navTreeItem !== null) {
    breadcrumbs.push({
      href: navTreeItem.href,
      title: navTreeItem.title || '',
      preventRedirect: navTreeItem.preventRedirect || false,
    });
    navTreeItem = findParentNavTreeItemBySlug(navTree, navTreeItem?.meta?.slug || '');
  }

  const rootItem = findNavTreeItemByHref(navTree, '/');
  if (rootItem) {
    breadcrumbs.push({
      href: rootItem.href,
      title: rootItem.title || '',
    });
  }

  return breadcrumbs.reverse();
}

export const findNavTitleByFlattenedPath = (navTree: NavTree, flattenedPath: string): string => {
  for (const navTreeItem of createTreeIterator<NavTreeItem>(navTree)) {
    const itemFlattenedPath = getFlattenedPathFromSourceFilePath(navTreeItem.path);
    if (flattenedPath === itemFlattenedPath) {
      return navTreeItem.title;
    }
  }

  return '';
};
